import { getAuthToken } from "../../../../utilities/requests";

/**
 * SSE streaming utility for the Operations Assistant.
 *
 * Extends the streamAIReportingAPI pattern with additional event types
 * for action proposals and session management.
 *
 * @param {string} url       - API endpoint URL
 * @param {object} params    - POST body (store_id, user_query, session_uuid, conversation_history)
 * @param {object} callbacks - Event handlers:
 *   onToken(text), onChartData(data), onThinking(text), onDone(payload), onError(msg),
 *   onActionProposal(proposal), onBulkActionProposal(proposals), onSessionInfo(info),
 *   onCredentialRequest(request), onRosterUploadRequest(request)
 */
export async function streamOpsAssistantAPI(url, params, callbacks, { signal } = {}) {
  const {
    onToken,
    onChartData,
    onThinking,
    onDone,
    onError,
    onActionProposal,
    onBulkActionProposal,
    onSessionInfo,
    onCredentialRequest,
    onRosterUploadRequest,
  } = callbacks;

  const token = getAuthToken();

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
    signal,
  });

  if (!response.ok) {
    const err = new Error(`Request failed with status ${response.status}`);
    err.status = response.status;
    throw err;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let receivedDone = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop();

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const jsonStr = line.slice(6).trim();
      if (!jsonStr) continue;

      let event;
      try {
        event = JSON.parse(jsonStr);
      } catch (parseErr) {
        continue;
      }

      if (event.type === "token") {
        onToken?.(event.text);
      } else if (event.type === "thinking") {
        onThinking?.(event.text);
      } else if (event.type === "chart_data") {
        onChartData?.(event.data);
      } else if (event.type === "session_info") {
        onSessionInfo?.(event);
      } else if (event.type === "action_proposal") {
        onActionProposal?.(event.proposal);
      } else if (event.type === "bulk_action_proposal") {
        onBulkActionProposal?.(event.proposals);
      } else if (event.type === "credential_request") {
        onCredentialRequest?.(event.request);
      } else if (event.type === "roster_upload_request") {
        onRosterUploadRequest?.(event.request);
      } else if (event.type === "done") {
        receivedDone = true;
        onDone?.(event);
      } else if (event.type === "error") {
        const errorMsg = event.text || "An unknown error occurred.";
        onError?.(errorMsg);
        throw new Error(errorMsg);
      }
    }
  }

  if (!receivedDone) {
    throw new Error("Stream ended unexpectedly without completing.");
  }
}
